import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Card, SectionTitle, StatusBadge, int, plural, ago } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CircleCheck, CircleX, TriangleAlert, CircleMinus, RefreshCw, GitBranch } from "lucide-react";

// Investigate, part 4 (backend/investigate.py, docs/INVESTIGATE_PLAN.md "Integrity checks"): the session's log held
// up against its other records: the proxy's billing records, the engine's own totals, and git as of the latest change
// report. Each check in a founder's words (what it checks, true when it passes), its status as an icon and a word, and
// for a problem or a warning the specifics: event numbers that jump to the event, SHAs and paths in mono, token counts
// side by side. Metadata only, like the rest of the page.

export const CHECK_WORDS = {
  chain: "Every event is as it was recorded",
  sequence: "No event is missing, and every turn ended",
  proxy_turns: "Every model call happened during a turn",
  proxy_log: "Every billed model call is in the log",
  proxy_engine: "The engine's own token count matches what was billed",
  runner_git: "Every commit on the branch was reported",
  files_report: "Every changed file is in the log",
};
// why a check didn't run (investigate.py, not_run reasons)
export const NOT_RUN_WORDS = {
  no_log: "No log to check yet",
  no_change_report: "Needs a change report",
  branch_merged: "The branch is already in its base branch: nothing left to compare",
  no_engine_totals: "The engine reported no totals of its own (MockEngine, a runner before 1.20.0, or Codex after a runner restart)",
};
const STATUS = {
  pass: ["Passed", "text-gr-success", CircleCheck],
  problem: ["Problem", "text-gr-danger", CircleX],
  warning: ["Warning", "text-gr-warning", TriangleAlert],
  not_run: ["Not run", "text-muted-foreground", CircleMinus],
};
const LIMIT = 20;  // investigate.LIMIT: event numbers, SHAs or paths listed per check

function Status({ status }) {
  const [word, tone, Icon] = STATUS[status] || STATUS.not_run;
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium uppercase tracking-[0.09em]", tone)}>
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />{word}
    </span>
  );
}

const LINK = "underline decoration-gr-hairline underline-offset-2 hover:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded-sm";

// a list of what a check found, joined by commas, with "and N more" when the check listed fewer than it found
function List({ items, total, render }) {
  const more = (total ?? items.length) - items.length;
  return (
    <>{items.map((x, i) => <span key={`${x}-${i}`}>{i > 0 && ", "}{render(x)}</span>)}
      {more > 0 && `, and ${int(more)} more`}{total == null && items.length >= LIMIT && " (the first 20)"}</>
  );
}

const Seq = ({ seq, reveal }) => (
  <button type="button" onClick={() => reveal(seq)} className={cn("font-mono tabular-nums text-foreground", LINK)}
    title={`Show event ${seq} in the timeline`}>{seq}</button>
);
const Mono = ({ children }) => <span className="font-mono text-foreground [overflow-wrap:anywhere]">{children}</span>;
const range = (g) => (g.from === g.to ? `${g.from}` : `${g.from}–${g.to}`);

// The token counts of each turn where the proxy and the engine disagree, side by side
function Tokens({ rows, caption }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span>{caption}</span>
      <div className="overflow-x-auto">
        <table className="text-sm tabular-nums border-separate border-spacing-x-4 -mx-4">
          <thead>
            <tr className="text-xs uppercase tracking-[0.09em] text-muted-foreground text-right">
              <th scope="col" className="text-left font-normal">Turn</th>
              <th scope="col" className="font-normal">Input billed</th><th scope="col" className="font-normal">Input, engine</th>
              <th scope="col" className="font-normal">Output billed</th><th scope="col" className="font-normal">Output, engine</th>
            </tr>
          </thead>
          <tbody className="font-mono text-right text-foreground">
            {rows.map((r) => (
              <tr key={r.turn}>
                <td className="text-left font-sans">{r.turn}</td>
                <td>{int(r.proxy_input)}</td><td>{int(r.engine_input)}</td>
                <td>{int(r.proxy_output)}</td><td>{int(r.engine_output)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// What a check found, or why it didn't run, in words
function Detail({ c, reveal, onBuild, building }) {
  if (c.status === "not_run") {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        {NOT_RUN_WORDS[c.reason] || "Didn't run"}
        {c.reason === "no_change_report" && (
          <button type="button" onClick={onBuild} disabled={building} className={cn("text-foreground disabled:opacity-40", LINK)}
            data-testid="integrity-build-report">{building ? "Building one…" : "Build one now"}</button>
        )}
      </span>
    );
  }
  const seqs = (list, total) => <List items={list || []} total={total} render={(s) => <Seq seq={s} reveal={reveal} />} />;
  const shas = (list) => <List items={list || []} render={(s) => <Mono>{String(s).slice(0, 7)}</Mono>} />;
  const lines = [];
  switch (c.check) {
    case "chain":
      if (c.edited?.length) lines.push(<>Changed after {c.edited.length === 1 ? "it was" : "they were"} written: event{c.edited.length === 1 ? "" : "s"} {seqs(c.edited)}</>);
      break;
    case "sequence":
      if (c.gaps?.length) lines.push(<>Missing from the log: event{c.gaps.length === 1 && c.gaps[0].from === c.gaps[0].to ? "" : "s"} <Mono>{c.gaps.map(range).join(", ")}</Mono></>);
      if (c.missing_turns?.length) lines.push(<>Counted, but with no events: turn{c.missing_turns.length === 1 ? "" : "s"} {c.missing_turns.join(", ")}</>);
      if (c.unfinished_turns?.length) lines.push(<>Never ended: turn{c.unfinished_turns.length === 1 ? "" : "s"} {c.unfinished_turns.join(", ")}</>);
      break;
    case "proxy_turns":
      if (c.count) lines.push(<>{plural(c.count, "model call")} between turns: event{c.count === 1 ? "" : "s"} {seqs(c.seqs, c.count)}</>);
      break;
    case "proxy_log":
      if (c.status === "pass") lines.push(<>{plural(c.billed || 0, "billed call")}, all in the log</>);
      else lines.push(
        <span className="inline-flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
          <span>Billed <span className="font-mono text-foreground">{int(c.billed)}</span></span>
          <span>In the log <span className="font-mono text-foreground">{int(c.logged)}</span></span>
          <span>{c.billed > c.logged
            ? `${plural(c.billed - c.logged, "billed call")} missing from the log`
            : `${plural(c.logged - c.billed, "logged call")} with no billing record`}</span>
        </span>,
      );
      break;
    case "proxy_engine":
      if (c.status === "pass") lines.push(<>Compared in {plural(c.turns || 0, "turn")}</>);
      if (c.engine_more?.length) lines.push(<Tokens rows={c.engine_more} caption="The engine counted more than was billed: calls the log doesn't have." />);
      if (c.proxy_more?.length) lines.push(<Tokens rows={c.proxy_more} caption="More was billed than the engine counted, in turns without subagents." />);
      break;
    case "runner_git":
      if (c.status === "pass") lines.push(<>{plural(c.commits || 0, "commit")} on the branch, all reported</>);
      if (c.unreported?.length) lines.push(<>On the branch, never reported: {shas(c.unreported)}</>);
      if (c.missing_from_log?.length) lines.push(<>Reported, but its log event is missing: {shas(c.missing_from_log)}</>);
      if (c.missing_from_git?.length) lines.push(<>Reported, but not on the branch any more (merged into its base, or the branch was restored): {shas(c.missing_from_git)}</>);
      break;
    case "files_report":
      if (c.status === "pass") lines.push(<>{plural(c.files || 0, "changed file")}, all in the log</>);
      if (c.missed?.length) lines.push(<>Changed on the branch, never seen in the log: <List items={c.missed} total={c.count} render={(p) => <Mono>{p}</Mono>} /></>);
      break;
    default:
      break;
  }
  if (!lines.length) return null;
  return <>{lines.map((l, i) => <div key={i} className="[overflow-wrap:anywhere]">{l}</div>)}</>;
}

// "All checks passed", "2 problems", "1 warning", "Not checked yet"
function overall(integrity) {
  if (!integrity) return ["Not checked yet", "muted"];
  const n = (s) => integrity.checks.filter((c) => c.status === s).length;
  if (n("problem")) return [plural(n("problem"), "problem"), "danger"];
  if (n("warning")) return [plural(n("warning"), "warning"), "warning"];
  if (n("not_run")) return [n("pass") ? "All that ran passed" : "Nothing could run", "muted"];
  return ["All checks passed", "info"];
}

// `engine`: the session's (a MockEngine report writes no AI summary, so it costs nothing); `reportLink`: where the
// founder reads the change report the checks used
export default function InvestigateIntegrity({ sid, integrity, engine, reveal, reportLink }) {
  const qc = useQueryClient();
  const refresh = () => ["investigation", "session-events", "investigate-sessions"].forEach((k) =>
    qc.invalidateQueries({ queryKey: k === "investigate-sessions" ? [k] : [k, sid] }));
  const check = useMutation({
    mutationFn: async () => (await api.post(`/sessions/${sid}/integrity`)).data,
    onSuccess: refresh,
    onError: (e) => toast.error(apiErr(e)),
  });
  // a fresh change report from the branch as it is now: building one runs the checks too
  const build = useMutation({
    mutationFn: async () => (await api.post(`/sessions/${sid}/report`)).data,
    onSuccess: () => { refresh(); toast.success("Change report built from the branch, and the log checked against it."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const busy = check.isPending || build.isPending;
  const [label, tone] = overall(integrity);
  const real = engine && engine !== "mock";

  return (
    <section className="flex flex-col gap-3" aria-labelledby="integrity-title" data-testid="integrity">
      <SectionTitle note={integrity ? `Checked ${ago(integrity.checked_at).toLowerCase()}` : "The log, held up against billing, the engine and git"}>
        <span id="integrity-title">Integrity</span>
      </SectionTitle>
      <Card className="p-0 overflow-hidden flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="integrity-overall">
            <StatusBadge label={label} tone={tone} />
            {integrity?.report_id && reportLink && (
              <Link to={reportLink(integrity.report_id)} className={cn("text-sm text-muted-foreground", LINK)}>Against the latest change report</Link>
            )}
          </span>
          <span className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => check.mutate()} disabled={busy} data-testid="integrity-check">
              <RefreshCw className={check.isPending ? "animate-spin" : ""} />{check.isPending ? "Checking…" : integrity ? "Check again" : "Check now"}
            </Button>
            <Button variant="outline" onClick={() => build.mutate()} disabled={busy} data-testid="integrity-branch">
              <GitBranch />{build.isPending ? "Building the report…" : "Check against the branch now"}
            </Button>
          </span>
        </div>
        {integrity ? (
          <ul className="m-0 p-0 list-none flex flex-col" data-testid="integrity-checks">
            {integrity.checks.map((c) => (
              <li key={c.check} className="flex flex-col gap-1.5 px-4 py-3 border-b last:border-b-0" data-testid={`integrity-${c.check}`}>
                <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <span className="min-w-0 [text-wrap:pretty]">{CHECK_WORDS[c.check] || c.check.replace(/_/g, " ")}</span>
                  <Status status={c.status} />
                </span>
                <span className="flex flex-col gap-1 text-sm text-muted-foreground">
                  <Detail c={c} reveal={reveal} onBuild={() => build.mutate()} building={build.isPending} />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 px-4 py-3 text-muted-foreground [text-wrap:pretty]" data-testid="integrity-unchecked">
            Not checked yet. A change report checks the log on its own, or check it now.
          </p>
        )}
        <p className="m-0 px-4 py-3 border-t text-sm text-muted-foreground [text-wrap:pretty]">
          Checking again compares the log with the latest change report and costs nothing. Checking against the branch
          builds a fresh change report first{real ? ", and its AI summary costs a little" : ""}.
        </p>
      </Card>
    </section>
  );
}
